/**
 * Frontend role/screen access helpers.
 * Built-in roles keep hardcoded SideMenu / page branches.
 * Dynamic roles use screens from login payload and ClubAdmin-like data scoping.
 */

export const BUILT_IN_ROLES = new Set([
  "SuperAdmin",
  "Admin",
  "ClubAdmin",
  "Trainer",
  "Member",
  "User",
]);

export function isMemberRole(role) {
  const key = String(role || "").toLowerCase();
  return key === "user" || key === "member";
}

export function isBuiltInRole(role) {
  if (!role) return false;
  return BUILT_IN_ROLES.has(role) || BUILT_IN_ROLES.has(
    // tolerate exact casing used in the app
    ["SuperAdmin", "Admin", "ClubAdmin", "Trainer", "Member", "User"].find(
      (r) => r.toLowerCase() === String(role).toLowerCase()
    )
  );
}

/**
 * True for Super-Admin-created roles (not built-in, not member portal).
 * Prefer is_dynamic from login when available.
 */
export function isDynamicStaffRole(role, userOrScreens) {
  if (!role || isMemberRole(role)) return false;

  if (userOrScreens && typeof userOrScreens === "object" && !Array.isArray(userOrScreens)) {
    if (userOrScreens.is_dynamic === true) return true;
    if (userOrScreens.is_dynamic === false) return false;
  }

  const builtIn = ["superadmin", "admin", "clubadmin", "trainer", "member", "user"];
  return !builtIn.includes(String(role).toLowerCase());
}

/**
 * Check whether the logged-in user has a screen key.
 * user may be the session user object with .screens / .is_dynamic / .role
 */
export const GYM_FEATURE_KEYS = [
  "dashboard",
  "locations",
  "members",
  "subscriptions",
  "facilities",
  "packages",
  "company_ledgers",
  "expense",
  "pos",
  "trainers",
  "assets",
  "staff_members",
  "staff_attendance",
  "visitors",
  "reporting",
  "settings",
];

export const SUPERADMIN_ALLOWED_PATHS = [
  "/dashboard",
  "/companies",
  "/admin/users",
  "/admin/roles",
  "/change-password",
];

export function isPlatformSuperAdmin(role) {
  return String(role || "").toLowerCase() === "superadmin";
}

export function isCompanyAdmin(role) {
  return String(role || "").toLowerCase() === "admin";
}

export function isGymFeatureKey(screenKey) {
  return GYM_FEATURE_KEYS.includes(screenKey);
}

export function hasScreen(user, screenKey) {
  if (!user || !screenKey) return false;
  const screens = Array.isArray(user.screens) ? user.screens : [];
  return screens.includes(screenKey);
}

/**
 * Action permission check. SuperAdmin always allowed.
 * Built-in Admin/ClubAdmin/Trainer use login `permissions` (full defaults).
 * Dynamic roles use explicit `permissions` from login payload.
 * Built-in staff with an empty/missing permissions array (pre-relogin sessions)
 * keep prior full access for Admin/ClubAdmin.
 */
export function hasPermission(user, permissionKey) {
  if (!user || !permissionKey) return false;
  if (isPlatformSuperAdmin(user.role)) return true;
  const permissions = Array.isArray(user.permissions) ? user.permissions : [];
  if (!permissions.length && !isDynamicStaffRole(user.role, user)) {
    const role = String(user.role || "").toLowerCase();
    if (role === "admin" || role === "clubadmin") return true;
    if (role === "trainer") return permissionKey === "dashboard:read";
    return false;
  }
  return permissions.includes(permissionKey);
}

export function hasAnyPermission(user, keys) {
  if (!Array.isArray(keys) || !keys.length) return false;
  return keys.some((k) => hasPermission(user, k));
}

/** Screen visible and readable. */
export function canReadScreen(user, screenKey) {
  if (!user || !screenKey) return false;
  if (isPlatformSuperAdmin(user.role)) return hasScreen(user, screenKey) || !isGymFeatureKey(screenKey);
  if (!hasScreen(user, screenKey) && isDynamicStaffRole(user.role, user)) return false;
  // Built-in Admin/ClubAdmin: screen access via SideMenu is enough for read
  if (!isDynamicStaffRole(user.role, user)) {
    if (["admin", "clubadmin"].includes(String(user.role || "").toLowerCase())) return true;
    if (String(user.role || "").toLowerCase() === "trainer") {
      return hasPermission(user, `${screenKey}:read`) || screenKey === "dashboard";
    }
  }
  return hasPermission(user, `${screenKey}:read`);
}

/**
 * Empty / missing enabled_features means all gym modules (backward compatible).
 */
export function getEnabledFeatures(user) {
  const fromUser = user?.enabled_features;
  const fromCompany = user?.company?.enabled_features;
  const list = Array.isArray(fromUser) && fromUser.length
    ? fromUser
    : Array.isArray(fromCompany)
      ? fromCompany
      : [];
  if (!list.length) return [...GYM_FEATURE_KEYS];
  return list;
}

export function hasCompanyFeature(user, screenKey) {
  if (!user || !screenKey) return false;
  if (isPlatformSuperAdmin(user.role)) return !isGymFeatureKey(screenKey);
  if (!isGymFeatureKey(screenKey)) return true;
  return getEnabledFeatures(user).includes(screenKey);
}

export function isSuperAdminPathAllowed(pathname) {
  if (!pathname) return false;
  return SUPERADMIN_ALLOWED_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

/**
 * Read session user from sessionStorage (same pattern as the rest of the app).
 */
export function getSessionUser() {
  try {
    const raw = sessionStorage.getItem("user-info");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.user || null;
  } catch {
    return null;
  }
}

/** Staff home route after login — first module they can read, else dashboard welcome. */
const STAFF_LANDING_ROUTES = [
  { screen: "dashboard", path: "/dashboard" },
  { screen: "subscriptions", path: "/ledgers" },
  { screen: "members", path: "/members" },
  { screen: "company_ledgers", path: "/company-ledgers" },
  { screen: "expense", path: "/expense" },
  { screen: "pos", path: "/pos" },
  { screen: "reporting", path: "/income-breakdown-report" },
  { screen: "staff_attendance", path: "/staffattendance" },
  { screen: "visitors", path: "/visitor" },
  { screen: "packages", path: "/packages" },
  { screen: "facilities", path: "/facilities" },
  { screen: "trainers", path: "/trainers" },
  { screen: "assets", path: "/assets" },
  { screen: "staff_members", path: "/staffmember" },
  { screen: "locations", path: "/locations" },
  { screen: "settings", path: "/settings" },
  { screen: "admin_users", path: "/admin/users" },
  { screen: "admin_roles", path: "/admin/roles" },
];

export function getStaffLandingPath(user) {
  if (!user) return "/dashboard";
  if (isPlatformSuperAdmin(user.role)) return "/dashboard";

  for (const route of STAFF_LANDING_ROUTES) {
    if (!hasCompanyFeature(user, route.screen)) continue;
    if (isDynamicStaffRole(user.role, user) && !hasScreen(user, route.screen)) {
      continue;
    }
    if (canReadScreen(user, route.screen)) {
      return route.path;
    }
  }
  return "/dashboard";
}

export function canViewDashboardOverview(user, role) {
  if (isPlatformSuperAdmin(role)) return true;
  if (!user) return false;
  return canReadScreen(user, "dashboard");
}

/**
 * Staff access including dynamic roles (ClubAdmin-like).
 * Does NOT replace canStaffAccess — use at call sites that should allow dynamic roles.
 */
export function canStaffOrDynamicAccess(role, user) {
  const key = String(role || "").toLowerCase();
  if (["superadmin", "admin", "clubadmin"].includes(key)) return true;
  return isDynamicStaffRole(role, user);
}
